import React, { useState, useEffect, useRef } from 'react';
import { fetchTournamentChatrooms, fetchChatMessages, sendChatMessage } from '../../api/ChatRoomApi';

const ChatRoomModal = ({ isOpen, onClose, tournamentId, tournamentName }) => {
  const [chatrooms, setChatrooms] = useState([]);
  const [selectedChatroom, setSelectedChatroom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const [messagePollingInterval, setMessagePollingInterval] = useState(null);
  
  // Get user coin balance from localStorage
  const [userCoins, setUserCoins] = useState(0);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUsername, setCurrentUsername] = useState('');
  
  // Track the last message timestamp for polling
  const [lastMessageTimestamp, setLastMessageTimestamp] = useState(null);
  
  // Add state for confirmation dialog
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [skipConfirmation, setSkipConfirmation] = useState(false);
  
  useEffect(() => {
    // Get player data from localStorage to retrieve coin balance and user ID
    const playerData = JSON.parse(localStorage.getItem("player_data") || "{}");
    setUserCoins(playerData.coin_balance || 0);
    setCurrentUserId(playerData.id || null);
    setCurrentUsername(playerData.username || '');
  }, []);
  
  // Load skip confirmation preference from localStorage
  useEffect(() => {
    const skipPref = localStorage.getItem("skip_chat_coin_confirmation");
    if (skipPref === "true") {
      setSkipConfirmation(true);
    }
  }, []);
  
  // Calculate max message length based on user coins and cost per letter
  const maxMessageLength = selectedChatroom ? Math.floor(userCoins / selectedChatroom.cost_per_letter) : 0;
  
  // Calculate remaining characters
  const remainingChars = Math.max(0, maxMessageLength - newMessage.length);
  
  // Calculate cost of current message
  const messageCost = selectedChatroom ? newMessage.length * selectedChatroom.cost_per_letter : 0;

  // Load chatrooms when component mounts or tournamentId changes
  useEffect(() => {
    const loadChatrooms = async () => {
      if (!isOpen || !tournamentId) return;
      
      try {
        setLoading(true);
        const chatroomsData = await fetchTournamentChatrooms(tournamentId);
        setChatrooms(chatroomsData);
        
        // Select the cheapest chatroom by default if available
        if (chatroomsData.length > 0) {
          const cheapestRoom = [...chatroomsData].sort((a, b) => 
            a.cost_per_letter - b.cost_per_letter
          )[0];
          setSelectedChatroom(cheapestRoom);
        }
        
        setError(null);
      } catch (err) {
        console.error("Failed to load chatrooms:", err);
        setError("Failed to load tournament chatrooms. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && tournamentId) {
      loadChatrooms();
    }
    
    return () => {
      // Clear polling interval when component unmounts
      if (messagePollingInterval) {
        clearInterval(messagePollingInterval);
      }
    };
  }, [isOpen, tournamentId]);

  // Load messages when selectedChatroom changes
  useEffect(() => {
    const loadMessages = async () => {
      if (!selectedChatroom || !isOpen) return;
      
      try {
        const messagesData = await fetchChatMessages(selectedChatroom.id, lastMessageTimestamp);
        
        // If we have new messages, add them to the existing ones
        if (messagesData && messagesData.length > 0) {
          // Check if we already have some of these messages to avoid duplicates
          const existingMessageIds = new Set(messages.map(msg => msg.id));
          const newMessages = messagesData.filter(msg => !existingMessageIds.has(msg.id));
          
          if (newMessages.length > 0) {
            // Sort all messages by timestamp to ensure correct order
            const allMessages = [...messages, ...newMessages].sort((a, b) => 
              new Date(a.timestamp) - new Date(b.timestamp)
            );
            
            setMessages(allMessages);
            
            // Update the last seen timestamp for the next poll
            // Use the most recent message timestamp
            if (allMessages.length > 0) {
              const latestMessage = allMessages[allMessages.length - 1];
              setLastMessageTimestamp(latestMessage.timestamp);
            }
          }
        }
        
        // Scroll to bottom after messages load
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      } catch (err) {
        console.error("Failed to load messages:", err);
        setError("Failed to load chatroom messages. Please try again later.");
      }
    };

    if (selectedChatroom && isOpen) {
      // Initial load of messages (with null timestamp to get recent history)
      loadMessages();
      
      // Set up polling for new messages every 5 seconds
      const intervalId = setInterval(loadMessages, 100000);
      setMessagePollingInterval(intervalId);
      
      return () => {
        clearInterval(intervalId);
        setMessagePollingInterval(null);
      };
    }
  }, [selectedChatroom, isOpen, messages, lastMessageTimestamp]);

  // Scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Check if a message is from the current user
  const isOwnMessage = (message) => {
    return message.sender_id === currentUserId;
  };

  // Get appropriate styling and badges for different message types
  const getMessageStyles = (message) => {
    if (message.user_details?.is_admin) {
      return {
        containerClass: "bg-red-50 border-red-200",
        textClass: "text-red-800",
        badge: {
          text: "ADMIN",
          class: "bg-red-600 text-white text-xs px-2 py-0.5 rounded ml-2"
        }
      };
    } else if (message.user_details?.is_partner) {
      return {
        containerClass: "bg-purple-50 border-purple-200",
        textClass: "text-purple-800",
        badge: {
          text: "PARTNER",
          class: "bg-purple-600 text-white text-xs px-2 py-0.5 rounded ml-2"
        }
      };
    } else if (isOwnMessage(message)) {
      return {
        containerClass: "bg-primary-100 border-primary-200",
        textClass: "text-primary-700",
        badge: null
      };
    } else {
      return {
        containerClass: "bg-white border-gray-200",
        textClass: "text-gray-700",
        badge: null
      };
    }
  };

  // Save skip confirmation preference to localStorage
  const saveSkipPreference = (skip) => {
    localStorage.setItem("skip_chat_coin_confirmation", skip.toString());
    setSkipConfirmation(skip);
  };
  
  // Handle sending a new message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChatroom) return;
    
    // Check if message is within length limit
    if (newMessage.length > maxMessageLength) {
      setError(`Message too long. You can send up to ${maxMessageLength} characters with your current coin balance.`);
      return;
    }
    
    // Show confirmation dialog if not skipped
    if (!skipConfirmation) {
      setShowConfirmation(true);
      return;
    }
    
    // Proceed with sending the message
    await sendMessageWithCoinDeduction();
  };
  
  // Function to actually send the message after confirmation
  const sendMessageWithCoinDeduction = async () => {
    try {
      setSendingMessage(true);
      const sentMessage = await sendChatMessage(selectedChatroom.id, newMessage);
      
      // Clear the message input
      setNewMessage('');
      
      // Update the last seen timestamp with the new message timestamp
      if (sentMessage && sentMessage.timestamp) {
        setLastMessageTimestamp(sentMessage.timestamp);
      }
      
      // Refresh messages
      const messagesData = await fetchChatMessages(selectedChatroom.id, lastMessageTimestamp);
      
      // Update messages list including the new message
      if (messagesData && messagesData.length > 0) {
        const existingMessageIds = new Set(messages.map(msg => msg.id));
        const newMessages = messagesData.filter(msg => !existingMessageIds.has(msg.id));
        
        if (newMessages.length > 0) {
          setMessages([...messages, ...newMessages].sort((a, b) => 
            new Date(a.timestamp) - new Date(b.timestamp)
          ));
        }
      }
      
      // Update user's coin balance based on message cost
      const newCoinBalance = userCoins - messageCost;
      setUserCoins(newCoinBalance);
      
      // Update player data in localStorage
      const playerData = JSON.parse(localStorage.getItem("player_data") || "{}");
      playerData.coin_balance = newCoinBalance;
      localStorage.setItem("player_data", JSON.stringify(playerData));
      
      setError(null);
    } catch (err) {
      console.error("Failed to send message:", err);
      setError("Failed to send your message. Please try again.");
    } finally {
      setSendingMessage(false);
    }
  };
  
  // Cancel sending message and close confirmation
  const cancelSendMessage = () => {
    setShowConfirmation(false);
  };
  
  // Confirm and send message
  const confirmSendMessage = (dontShowAgain) => {
    // If "don't show again" is checked, save preference
    if (dontShowAgain) {
      saveSkipPreference(true);
    }
    
    setShowConfirmation(false);
    sendMessageWithCoinDeduction();
  };

  // Handle chatroom selection
  const handleChatroomChange = (chatroomId) => {
    const selected = chatrooms.find(room => room.id === chatroomId);
    setSelectedChatroom(selected);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[100vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-primary-700 to-primary-800 text-white px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-bold">
            {tournamentName ? `${tournamentName} Chat` : 'Tournament Chat'}
          </h2>
          <button 
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Chatroom Selector */}
        {chatrooms.length > 1 && (
          <div className="p-4 border-b bg-gray-50">
            <label htmlFor="chatroom-select" className="block text-sm font-medium text-gray-700 mb-1">
              Select Chatroom
            </label>
            <select
              id="chatroom-select"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              value={selectedChatroom?.id || ''}
              onChange={(e) => handleChatroomChange(e.target.value)}
            >
              {chatrooms.map((room) => (
                <option key={room.id} value={room.id}>
                  Room {room.id.substring(0, 6)} ({room.cost_per_letter} coins per character)
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Messages Area */}
        <div className="flex-grow overflow-y-auto p-4 bg-gray-50" style={{ maxHeight: '60vh' }}>
          {messages.length > 0 ? (
            <div className="space-y-3">
              {messages.map((message, index) => {
                const isOwn = isOwnMessage(message);
                const styles = getMessageStyles(message);

                return (
                  <div key={index} className={`flex items-start ${isOwn ? 'justify-end' : ''}`}>
                    {!isOwn && (
                      <div className="flex-shrink-0">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white font-medium ${
                          message.user_details?.is_admin ? 'bg-red-600' : 
                          message.user_details?.is_partner ? 'bg-purple-600' : 
                          'bg-primary-600'
                        }`}>
                          {message.sender_name?.charAt(0).toUpperCase() || '?'}
                        </div>
                      </div>
                    )}

                    <div className={`${isOwn ? 'mr-0' : 'ml-3'} ${styles.containerClass} p-3 rounded-lg shadow-sm border`}>
                      <div className="flex items-baseline mb-1">
                        <p className={`text-sm font-medium mr-2 ${isOwn ? 'text-primary-800' : 'text-gray-900'}`}>
                          {isOwn ? 'You' : (message.sender_name || 'Unknown User')}
                        </p>
                        {styles.badge && (
                          <span className={styles.badge.class}>{styles.badge.text}</span>
                        )}
                        <p className="text-xs text-gray-500 ml-auto">
                          {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <p className={`text-sm ${styles.textClass}`}>{message.text}</p>
                    </div>

                    {isOwn && (
                      <div className="flex-shrink-0 ml-3">
                        <div className="h-8 w-8 rounded-full bg-primary-700 flex items-center justify-center text-white font-medium">
                          {currentUsername.charAt(0).toUpperCase() || 'Y'}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="text-3xl mb-3">💬</div>
              <p className="text-gray-500 mb-1">No messages yet</p>
              <p className="text-sm text-gray-400">Be the first to say something!</p>
            </div>
          )}
        </div>

        {/* Coin Deduction Confirmation Dialog */}
        {showConfirmation && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 animate-fade-in-up">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Confirm Coin Deduction</h3>
              <p className="text-sm text-gray-600 mb-4">
                Sending this message will cost you <span className="font-medium text-primary-600">{messageCost} coins</span>. 
                Would you like to proceed?
              </p>
              
              <div className="flex items-center mb-4">
                <input
                  id="dont-show-again"
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  onChange={(e) => setSkipConfirmation(e.target.checked)}
                />
                <label htmlFor="dont-show-again" className="ml-2 text-sm text-gray-600">
                  Don't show this again
                </label>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  onClick={cancelSendMessage}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  onClick={() => confirmSendMessage(skipConfirmation)}
                >
                  Confirm Deduction
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Message Input */}
        <div className="p-4 border-t border-gray-200 bg-white">
          <div className="flex items-center space-x-2">
            <textarea
              rows="1"
              className={`flex-grow px-3 py-2 border ${
                newMessage.length > maxMessageLength ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
              } rounded-md shadow-sm text-sm`}
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              disabled={sendingMessage || !selectedChatroom}
            ></textarea>

            <button
              type="button"
              className={`px-4 py-2 border text-sm font-medium rounded-md shadow-sm ${
                sendingMessage || !newMessage.trim() || newMessage.length > maxMessageLength || !selectedChatroom
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
              }`}
              onClick={handleSendMessage}
              disabled={sendingMessage || !newMessage.trim() || newMessage.length > maxMessageLength || !selectedChatroom}
            >
              {sendingMessage ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending
                </div>
              ) : 'Send'}
            </button>
          </div>

          {/* Remaining Characters and Cost */}
          <div className="flex justify-between items-center text-xs text-gray-500 mt-2">
            <span className={remainingChars === 0 ? 'text-red-500 font-medium' : ''}>
              {remainingChars} characters remaining
            </span>
            <span className={messageCost > userCoins ? 'text-red-500 font-medium' : ''}>
              Cost: {messageCost} coins
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatRoomModal;
